import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getHomework, getStudentAnswers, submitHomework } from "../api/client";

export default function Homework() {
  const { classId } = useParams<{ classId: string }>();
  const [data, setData] = useState<any>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [nameInput, setNameInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeAssignment, setActiveAssignment] = useState<any>(null);
  const [error, setError] = useState("");
  const [errorTitle, setErrorTitle] = useState("");

  // After first submit — unlocks dashboard
  const [studentId, setStudentId] = useState("");
  const [savedAnswers, setSavedAnswers] = useState<Record<string, { answer_text: string; submitted_at: string }>>({});

  useEffect(() => {
    if (!classId) return;
    getHomework(classId).then((r) => setData(r.data)).catch((e: any) => {
      if (e?.response?.status === 404) {
        setErrorTitle("Not Found");
        setError("Class not found. The link may be outdated — please ask your teacher for the latest one.");
      } else {
        setErrorTitle("Connection Problem");
        setError("Couldn't reach the server. Try switching to Wi-Fi, opening the link in Chrome, or changing your phone's DNS to 1.1.1.1.");
      }
    });
  }, [classId]);

  // Restore draft answers when opening an assignment
  useEffect(() => {
    if (!classId || !activeAssignment) return;
    const raw = localStorage.getItem(`mot-draft-${classId}-${activeAssignment.id}`);
    if (raw) {
      try { setAnswers(JSON.parse(raw)); } catch { setAnswers({}); }
    } else {
      setAnswers({});
    }
  }, [classId, activeAssignment]);

  // Persist draft as the student types
  useEffect(() => {
    if (!classId || !activeAssignment) return;
    if (Object.keys(answers).length > 0) {
      localStorage.setItem(`mot-draft-${classId}-${activeAssignment.id}`, JSON.stringify(answers));
    }
  }, [classId, activeAssignment, answers]);

  const handleSubmit = async () => {
    if (!classId || !activeAssignment || !nameInput.trim()) return;
    // Match name to student
    const match = data.students.find((s: any) => s.name.toLowerCase() === nameInput.trim().toLowerCase());
    if (!match) {
      alert("Name not found. Please check your spelling.");
      return;
    }
    const unanswered = activeAssignment.questions.filter((q: any) => !answers[q.id]?.trim());
    if (unanswered.length > 0) {
      alert(`Please answer all questions. ${unanswered.length} question(s) not answered.`);
      return;
    }
    if (!nameInput.trim()) {
      alert("Please enter your name.");
      return;
    }
    const questionAnswers = activeAssignment.questions
      .map((q: any) => ({ question_id: q.id, answer_text: answers[q.id] }));
    setSaving(true);
    try {
      await submitHomework(classId, { student_id: match.id, answers: questionAnswers });
      localStorage.removeItem(`mot-draft-${classId}-${activeAssignment.id}`);
      setStudentId(match.id);
      // Load their dashboard
      const r = await getStudentAnswers(classId, match.id);
      setSavedAnswers(r.data);
      setActiveAssignment(null);
      setAnswers({});
    } catch (e) {
      alert("Failed to save. Check your connection.");
    }
    setSaving(false);
  };

  const getAnsweredCount = (assignment: any) => {
    return assignment.questions.filter((q: any) => savedAnswers[q.id]).length;
  };

  if (error) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ textAlign: "center", color: "#78716c" }}>
        <h2 style={{ fontFamily: "'Fraunces', serif", marginBottom: 8 }}>{errorTitle || "Not Found"}</h2>
        <p style={{ maxWidth: 360, margin: "0 auto", lineHeight: 1.5 }}>{error}</p>
      </div>
    </div>
  );

  if (!data) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif", color: "#78716c" }}>
      Loading...
    </div>
  );

  const headerStyle: React.CSSProperties = { background: "#1c1917", color: "#fff", padding: "24px 0" };
  const containerStyle: React.CSSProperties = { maxWidth: 700, margin: "0 auto", padding: "0 20px" };

  return (
    <div style={{ minHeight: "100vh", background: "#faf8f5", fontFamily: "'DM Sans', sans-serif" }}>
      <div style={headerStyle}>
        <div style={containerStyle}>
          <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: 24, fontWeight: 600 }}>Homework Portal</h1>
        </div>
      </div>

      <div style={{ ...containerStyle, padding: "24px 20px" }}>

        {/* === ANSWERING AN ASSIGNMENT (before submit) === */}
        {activeAssignment ? (
          <>
            <button
              onClick={() => setActiveAssignment(null)}
              style={{ background: "none", border: "none", color: "#78716c", fontSize: 13, cursor: "pointer", fontFamily: "inherit", marginBottom: 16, padding: 0 }}
            >
              &larr; Back
            </button>

            <div style={{ background: "#fff", borderRadius: 14, padding: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                <div>
                  <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600 }}>{activeAssignment.title}</h3>
                  {activeAssignment.description && <p style={{ color: "#78716c", fontSize: 13, marginTop: 4 }}>{activeAssignment.description}</p>}
                </div>
                {activeAssignment.due_date && (
                  <span style={{ fontSize: 12, color: "#92400e", background: "#fef3c7", padding: "4px 10px", borderRadius: 6, fontWeight: 500, whiteSpace: "nowrap" }}>
                    Due: {activeAssignment.due_date}
                  </span>
                )}
              </div>

              {activeAssignment.questions.map((q: any, i: number) => (
                <div key={q.id} style={{ marginBottom: 20 }}>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, color: "#1c1917", marginBottom: 8 }}>
                    Q{i + 1}. {q.question_text}
                  </label>
                  <textarea
                    value={answers[q.id] || ""}
                    onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                    placeholder="Type your answer..."
                    style={{
                      width: "100%", padding: "12px 14px", borderRadius: 8,
                      border: "1px solid #e7e5e4", fontSize: 14, fontFamily: "inherit",
                      minHeight: 80, resize: "vertical", background: "#fff",
                    }}
                  />
                </div>
              ))}

              {/* Name field */}
              <div style={{ marginBottom: 20, paddingTop: 16, borderTop: "1px solid #e7e5e4" }}>
                <label style={{ display: "block", fontSize: 14, fontWeight: 600, color: "#1c1917", marginBottom: 8 }}>
                  Your Name
                </label>
                <input
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  placeholder="Type your name..."
                  style={{
                    width: "100%", padding: "12px 14px", borderRadius: 8,
                    border: "1px solid #e7e5e4", fontSize: 16, fontFamily: "inherit",
                  }}
                />
              </div>

              <button
                onClick={handleSubmit}
                disabled={saving || !nameInput.trim()}
                style={{
                  width: "100%", padding: "14px", borderRadius: 8, border: "none",
                  background: saving || !nameInput.trim() ? "#a8a29e" : "#0d9488",
                  color: "#fff", fontSize: 16, fontWeight: 600,
                  cursor: saving || !nameInput.trim() ? "not-allowed" : "pointer",
                  fontFamily: "inherit", boxShadow: "0 2px 8px rgba(13,148,136,0.35)",
                }}
              >
                {saving ? "Submitting..." : "Submit Answers"}
              </button>
            </div>
          </>
        ) : (
          /* === ASSIGNMENT LIST === */
          <>
            {studentId && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <div>
                  <span style={{ fontSize: 13, color: "#78716c" }}>Welcome,</span>
                  <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 20 }}>
                    {data.students.find((s: any) => s.id === studentId)?.name}
                  </h2>
                </div>
              </div>
            )}

            {data.assignments.length === 0 ? (
              <div style={{ background: "#fff", borderRadius: 14, padding: 48, textAlign: "center", color: "#a8a29e" }}>
                No homework assigned yet.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 12 }}>
                {data.assignments.map((a: any) => {
                  const answered = getAnsweredCount(a);
                  const total = a.questions.length;
                  const isComplete = studentId && total > 0 && answered === total;
                  return (
                    <button
                      key={a.id}
                      onClick={() => setActiveAssignment(a)}
                      style={{
                        background: "#fff", borderRadius: 14, padding: "20px 24px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                        border: isComplete ? "2px solid #16a34a" : "1px solid #e7e5e4",
                        cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                      }}
                    >
                      <div>
                        <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 17, fontWeight: 600, marginBottom: 4 }}>{a.title}</h3>
                        <div style={{ fontSize: 13, color: "#78716c" }}>
                          {total} question{total !== 1 ? "s" : ""}
                          {a.due_date && <span> &middot; Due: {a.due_date}</span>}
                        </div>
                      </div>
                      {studentId && (
                        <div>
                          {isComplete ? (
                            <span style={{ background: "#dcfce7", color: "#166534", padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                              Completed
                            </span>
                          ) : answered > 0 ? (
                            <span style={{ background: "#fef3c7", color: "#92400e", padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                              {answered}/{total}
                            </span>
                          ) : (
                            <span style={{ background: "#f5f5f4", color: "#78716c", padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                              Not started
                            </span>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
